import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit, where, Timestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { FiTrendingUp, FiTrendingDown, FiShoppingCart, FiUsers, FiPackage, FiClock, FiCalendar, FiArrowUp, FiArrowDown, FiXCircle, FiPercent, FiRotateCcw, FiTag, FiFileText, FiDollarSign, FiCreditCard, FiSmartphone, FiAward, FiStar, FiUserCheck, FiMaximize, FiMinimize } from 'react-icons/fi';
import AnimatedCounter from '../components/AnimatedCounter';

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalSales: 0,
    grossSales: 0,
    netSales: 0,
    voidedSales: 0,
    voidPercentage: 0,
    returns: 0,
    discounts: 0,
    totalOrders: 0,
    totalCustomers: 0,
    totalProducts: 0,
    activeProducts: 0,
    inactiveProducts: 0,
    inStock: 0,
    lowStock: 0,
    noStock: 0,
    highStock: 0,
    cashSales: 0,
    cardSales: 0,
    upiSales: 0,
    otherPayments: 0,
    highValueCustomers: 0,
    mediumValueCustomers: 0,
    lowValueCustomers: 0,
    // Growth comparison stats
    lastWeekSales: 0,
    lastWeekGrowth: 0,
    lastMonthSales: 0,
    lastMonthGrowth: 0,
    lastYearSales: 0,
    lastYearGrowth: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [outOfStockProducts, setOutOfStockProducts] = useState([]);
  const [highStockProducts, setHighStockProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  
  // Helper function to get local date string (YYYY-MM-DD) without timezone conversion
  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Always use today's date - no user selection
  const selectedDate = getLocalDateString(new Date());
  
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const ordersPerPage = 5;
  
  // Pagination for stock tables
  const [lowStockCurrentPage, setLowStockCurrentPage] = useState(1);
  const [highStockCurrentPage, setHighStockCurrentPage] = useState(1);
  const stockItemsPerPage = 5;
  
  // Search and filters for stock tables
  const [lowStockSearchTerm, setLowStockSearchTerm] = useState('');
  const [lowStockStatusFilter, setLowStockStatusFilter] = useState('all'); // all, out-of-stock, low-stock
  const [lowStockCategoryFilter, setLowStockCategoryFilter] = useState('all');
  const [lowStockSortField, setLowStockSortField] = useState('name');
  const [lowStockSortDirection, setLowStockSortDirection] = useState('asc');
  
  const [highStockSearchTerm, setHighStockSearchTerm] = useState('');
  const [highStockCategoryFilter, setHighStockCategoryFilter] = useState('all');
  const [highStockSortField, setHighStockSortField] = useState('name');
  const [highStockSortDirection, setHighStockSortDirection] = useState('asc');
  
  const { selectedHierarchy } = useAuth();
  const { isDark } = useTheme();

  useEffect(() => {
    if (!selectedHierarchy?.outlet?.id) return;
    
    const outletId = selectedHierarchy.outlet.id;
    
    // Subscribe to invoices collection for real-time updates
    const invoicesQuery = query(
      collection(db, 'invoices'),
      where('outletId', '==', outletId)
    );
    
    const unsubscribeInvoices = onSnapshot(invoicesQuery, async (snapshot) => {
      const allInvoices = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      await processInvoicesData(allInvoices, outletId);
    }, (error) => {
      console.error('Error subscribing to invoices:', error);
      setLoading(false);
    });
    
    // Subscribe to products collection for real-time updates
    const productsQuery = query(
      collection(db, 'products'),
      where('outletId', '==', outletId)
    );
    
    const unsubscribeProducts = onSnapshot(productsQuery, async (snapshot) => {
      // When products change, reload all invoice data to recalculate stats
      const invoicesSnapshot = await getDocs(query(
        collection(db, 'invoices'),
        where('outletId', '==', outletId)
      ));
      const allInvoices = invoicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      await processInvoicesData(allInvoices, outletId);
    }, (error) => {
      console.error('Error subscribing to products:', error);
    });
    
    // Clean up subscriptions on unmount
    return () => {
      unsubscribeInvoices();
      unsubscribeProducts();
    };
  }, [selectedHierarchy, selectedDate]);

  const processInvoicesData = async (allInvoices, outletId) => {
    try {
      setLoading(true);
      
      console.log('Loading dashboard for today:', selectedDate);
      console.log('OutletId:', outletId);
      console.log('All invoices for outlet:', allInvoices.length);
      
      // Get ALL invoices for this outlet (already provided by subscription)
      
      // If no invoices, check if we have any invoices at all
      if (allInvoices.length === 0) {
        console.log('No invoices found. Checking if invoices collection exists...');
        const allInvoicesSnap = await getDocs(collection(db, 'invoices'));
        console.log('Total invoices in database:', allInvoicesSnap.size);
        if (allInvoicesSnap.size > 0) {
          const sampleInvoice = allInvoicesSnap.docs[0].data();
          console.log('Sample invoice structure:', sampleInvoice);
          console.log('Sample invoice fields:', Object.keys(sampleInvoice));
        }
      } else {
        // Log sample invoice dates to see the format
        console.log('Sample invoice:', allInvoices[0]);
        console.log('Sample invoice fields:', Object.keys(allInvoices[0]));
        console.log('Sample invoice createdAt:', allInvoices[0].createdAt);
        console.log('Sample invoice date:', allInvoices[0].date);
        console.log('Sample invoice invoiceDate:', allInvoices[0].invoiceDate);
        console.log('Sample invoice timestamp:', allInvoices[0].timestamp);
        
        // Log payment method fields
        console.log('=== PAYMENT METHOD IDENTIFICATION ===');
        console.log('Sample invoice paymentMethod:', allInvoices[0].paymentMethod);
        console.log('Sample invoice paymentType:', allInvoices[0].paymentType);
        console.log('Sample invoice payment:', allInvoices[0].payment);
        console.log('Sample invoice paymentMode:', allInvoices[0].paymentMode);
        console.log('Sample invoice paymentInfo:', allInvoices[0].paymentInfo);
        console.log('Sample invoice paymentBreakdown:', allInvoices[0].paymentBreakdown);
        
        // Check all unique payment method values
        const paymentMethods = new Set();
        const paymentTypes = new Set();
        allInvoices.forEach(inv => {
          if (inv.paymentMethod) paymentMethods.add(inv.paymentMethod);
          if (inv.paymentType) paymentTypes.add(inv.paymentType);
          if (inv.payment?.method) paymentMethods.add(inv.payment.method);
          if (inv.payment?.type) paymentTypes.add(inv.payment.type);
          // paymentBreakdown is an object with payment methods as keys
          if (inv.paymentBreakdown && typeof inv.paymentBreakdown === 'object') {
            Object.keys(inv.paymentBreakdown).forEach(method => {
              paymentMethods.add(method);
            });
          }
        });
        console.log('All unique paymentMethod values:', Array.from(paymentMethods));
        console.log('All unique paymentType values:', Array.from(paymentTypes));
        console.log('=== END PAYMENT METHOD IDENTIFICATION ===');
      }
      
      // Filter invoices by selected date on client side
      const invoices = allInvoices.filter(invoice => {
        if (!invoice.timestamp) return false;
        
        // Handle different date formats
        let invoiceDate;
        if (invoice.timestamp.toDate) {
          // Firestore Timestamp
          invoiceDate = invoice.timestamp.toDate();
        } else if (typeof invoice.timestamp === 'string') {
          // UTC string
          invoiceDate = new Date(invoice.timestamp);
        } else if (invoice.timestamp instanceof Date) {
          // JavaScript Date
          invoiceDate = invoice.timestamp;
        } else {
          return false;
        }
        
        // Convert to local date string (YYYY-MM-DD) without timezone issues
        const invoiceDateStr = getLocalDateString(invoiceDate);
        
        // Filter by today's date only
        return invoiceDateStr === selectedDate;
      });
      
      console.log('Invoices found for today:', invoices.length);
      console.log('Invoices data:', invoices);
      
      // Debug timezone info
      if (allInvoices.length > 0) {
        const sampleInvoice = allInvoices[0];
        const sampleDate = sampleInvoice.timestamp?.toDate?.() || new Date(sampleInvoice.timestamp);
        console.log('=== TIMEZONE DEBUG ===');
        console.log('Selected date:', selectedDate);
        console.log('Sample invoice timestamp:', sampleInvoice.timestamp);
        console.log('Sample invoice date (JS Date):', sampleDate);
        console.log('Sample invoice date (Local string):', getLocalDateString(sampleDate));
        console.log('Sample invoice date (UTC ISO):', sampleDate.toISOString());
        console.log('Browser timezone offset (minutes):', new Date().getTimezoneOffset());
        console.log('Browser timezone:', Intl.DateTimeFormat().resolvedOptions().timeZone);
        console.log('=== END TIMEZONE DEBUG ===');
      }
      
      // Debug: Check what fields are in the invoices
      if (invoices.length > 0) {
        console.log('Sample invoice structure:', invoices[0]);
        console.log('Sample invoice fields:', Object.keys(invoices[0]));
        console.log('Sample invoice total:', invoices[0].total);
        console.log('Sample invoice totalAmount:', invoices[0].totalAmount);
        console.log('Sample invoice amount:', invoices[0].amount);
      }
      
      // Debug: Check what status values exist in the data
      const statusCounts = invoices.reduce((acc, inv) => {
        const status = inv.status || 'no-status';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
      console.log('Status distribution:', statusCounts);
      console.log('All unique statuses:', Object.keys(statusCounts));
      
      // Calculate different sales metrics
      const completedInvoices = invoices.filter(inv => inv.status === 'completed');
      const voidedInvoices = invoices.filter(inv => inv.status === 'cancelled' || inv.status === 'voided' || inv.status === 'void');
      const returnedInvoices = invoices.filter(inv => inv.status === 'returned' || inv.status === 'refunded');
      
      console.log('Completed invoices count:', completedInvoices.length);
      console.log('Voided invoices count:', voidedInvoices.length);
      console.log('Returned invoices count:', returnedInvoices.length);
      
      // Show sample voided invoices if any exist
      if (voidedInvoices.length > 0) {
        console.log('Sample voided invoice:', voidedInvoices[0]);
        console.log('Voided invoice status:', voidedInvoices[0].status);
        console.log('Voided invoice totalAmount:', voidedInvoices[0].totalAmount);
      }
      
      const grossSales = invoices.reduce((sum, invoice) => sum + (invoice.totalAmount || 0), 0);
      const voidedSales = voidedInvoices.reduce((sum, invoice) => sum + (invoice.totalAmount || 0), 0);
      const netSales = completedInvoices.reduce((sum, invoice) => sum + (invoice.totalAmount || 0), 0);
      const returns = returnedInvoices.reduce((sum, invoice) => sum + (invoice.totalAmount || 0), 0);
      
      // Calculate discounts - check multiple possible locations
      let discounts = 0;
      
      invoices.forEach(invoice => {
        // Check top-level discount fields
        const topLevelDiscount = invoice.discountAmount || 
                                 invoice.discount || 
                                 invoice.totalDiscount || 
                                 invoice.discounts || 
                                 0;
        discounts += topLevelDiscount;
        
        // Check if discount is in items array
        if (invoice.items && Array.isArray(invoice.items)) {
          invoice.items.forEach(item => {
            const itemDiscount = item.discount || 
                                item.discountAmount || 
                                item.discountValue || 
                                0;
            discounts += itemDiscount;
          });
        }
      });
      
      // Log discount data for debugging
      console.log('=== DISCOUNT CALCULATION DEBUG ===');
      const sampleInvoice = invoices[0];
      if (sampleInvoice) {
        console.log('Sample invoice for discount check:', {
          id: sampleInvoice.id,
          discountAmount: sampleInvoice.discountAmount,
          discount: sampleInvoice.discount,
          totalDiscount: sampleInvoice.totalDiscount,
          items: sampleInvoice.items
        });
        
        // Log full item structure
        if (sampleInvoice.items && sampleInvoice.items.length > 0) {
          console.log('First item full structure:', sampleInvoice.items[0]);
          console.log('First item fields:', Object.keys(sampleInvoice.items[0]));
        }
      }
      console.log('Total discounts calculated:', discounts);
      console.log('=== END DISCOUNT DEBUG ===');
      
      const voidPercentage = grossSales > 0 ? (voidedSales / grossSales) * 100 : 0;
      const totalSales = netSales; // Total sales = Net sales (completed only)
      
      // Calculate payment type breakdown using paymentBreakdown object (only for completed invoices)
      let cashSales = 0;
      let cardSales = 0;
      let upiSales = 0;
      let otherPayments = 0;
      
      completedInvoices.forEach(invoice => {
        if (invoice.paymentBreakdown && typeof invoice.paymentBreakdown === 'object') {
          // paymentBreakdown is an object like {Cash: 50, Card: 30}
          Object.entries(invoice.paymentBreakdown).forEach(([method, amount]) => {
            const methodLower = method.toLowerCase();
            const paymentAmount = Number(amount) || 0;
            
            if (methodLower === 'cash') {
              cashSales += paymentAmount;
            } else if (methodLower === 'card' || methodLower === 'credit card' || methodLower === 'debit card' || methodLower.includes('card')) {
              cardSales += paymentAmount;
            } else if (methodLower === 'upi' || methodLower.includes('upi') || methodLower.includes('gpay') || methodLower.includes('phonepe') || methodLower.includes('paytm')) {
              upiSales += paymentAmount;
            } else {
              otherPayments += paymentAmount;
            }
          });
        }
      });
      
      console.log('Total sales calculated:', totalSales);
      console.log('Gross sales:', grossSales);
      console.log('Net sales:', netSales);
      console.log('Voided sales:', voidedSales);
      console.log('Returns:', returns);
      console.log('Discounts:', discounts);
      console.log('Payment breakdown - Cash:', cashSales, 'Card:', cardSales, 'UPI:', upiSales, 'Other:', otherPayments);
      console.log('Void %:', voidPercentage);
      
      // Calculate customer spending and segment them using DATE-FILTERED completed invoices
      const customerSpending = new Map();
      
      // Use completedInvoices (date-filtered) to get customer spending for the selected period
      console.log('Completed invoices for selected date:', completedInvoices.length);
      
      let customersWithId = 0;
      let customersWithoutId = 0;
      let walkInCustomerCounter = 0;
      
      completedInvoices.forEach(invoice => {
        if (invoice.customerId && invoice.customerId !== null) {
          customersWithId++;
          const currentSpending = customerSpending.get(invoice.customerId) || 0;
          customerSpending.set(invoice.customerId, currentSpending + (invoice.totalAmount || 0));
        } else {
          // Count each invoice without customerId as a separate walk-in customer
          customersWithoutId++;
          const walkInId = `walk-in-${invoice.id}`;
          customerSpending.set(walkInId, invoice.totalAmount || 0);
          walkInCustomerCounter++;
        }
      });
      
      console.log('Invoices with customerId:', customersWithId);
      console.log('Invoices without customerId (walk-in/anonymous):', customersWithoutId);
      console.log('Walk-in customers counted:', walkInCustomerCounter);
      console.log('Sample customerIds from invoices:', Array.from(customerSpending.keys()).slice(0, 5));
      
      // Segment customers based on total spending in the selected date range
      let highValueCustomers = 0;
      let mediumValueCustomers = 0;
      let lowValueCustomers = 0;
      
      customerSpending.forEach((totalSpent) => {
        if (totalSpent > 100) {
          highValueCustomers++;
        } else if (totalSpent >= 50) {
          mediumValueCustomers++;
        } else if (totalSpent >= 1) {
          lowValueCustomers++;
        }
      });
      
      console.log('Total unique customers with purchases in selected date:', customerSpending.size);
      console.log('Customer Segmentation - High Value (>100):', highValueCustomers, 'Medium Value (50-99):', mediumValueCustomers, 'Low Value (1-49):', lowValueCustomers);
      
      // Get unique customers who made purchases on the selected date
      const uniqueCustomerIds = new Set(
        invoices
          .filter(invoice => invoice.customerId) // Only count invoices with a customer ID
          .map(invoice => invoice.customerId)
      );
      
      // Total unique customers is from lifetime spending
      const totalUniqueCustomers = customerSpending.size;
      
      // Get total products count (not filtered by date)
      const productsSnap = await getDocs(query(collection(db, 'products'), where('outletId', '==', outletId)));
      
      // Fetch categories to map category names
      const categoriesSnap = await getDocs(query(collection(db, 'categories'), where('outletId', '==', outletId)));
      const categoryMap = {};
      const categoriesList = [];
      categoriesSnap.docs.forEach(doc => {
        const cat = doc.data();
        const categoryName = cat.name || cat.title || 'Uncategorized';
        categoryMap[doc.id] = categoryName;
        categoriesList.push({ id: doc.id, name: categoryName });
      });
      
      // Store categories in state for filter dropdown
      setCategories(categoriesList);
      
      // Calculate active and inactive products
      const allProducts = productsSnap.docs.map(doc => {
        const productData = doc.data();
        return {
          id: doc.id,
          ...productData,
          category: categoryMap[productData.categoryId] || 'Uncategorized'
        };
      });
      const activeProducts = allProducts.filter(product => 
        product.isActive === true || product.isActive === 1 || product.status === 'active'
      ).length;
      const inactiveProducts = allProducts.filter(product => 
        product.isActive === false || product.isActive === 0 || product.status === 'inactive'
      ).length;
      
      console.log('Product Stats:', {
        total: productsSnap.size,
        active: activeProducts,
        inactive: inactiveProducts,
        sampleProduct: allProducts[0] ? {
          id: allProducts[0].id,
          isActive: allProducts[0].isActive,
          status: allProducts[0].status,
          categoryId: allProducts[0].categoryId,
          category: allProducts[0].category
        } : null
      });

      // Get stock data for products
      // Query for stock with matching outletId OR null outletId (legacy data)
      const stockQuery1 = query(collection(db, 'stock'), where('outletId', '==', outletId));
      const stockQuery2 = query(collection(db, 'stock'), where('outletId', '==', null));
      
      const [stockSnap1, stockSnap2] = await Promise.all([
        getDocs(stockQuery1),
        getDocs(stockQuery2)
      ]);
      
      const stockMap = {};
      
      // Process stock with matching outletId
      stockSnap1.docs.forEach(doc => {
        const stockData = doc.data();
        stockMap[stockData.productId] = stockData.quantity || 0;
      });
      
      // Process stock with null outletId (legacy/global stock)
      stockSnap2.docs.forEach(doc => {
        const stockData = doc.data();
        // Only use null outletId stock if no outlet-specific stock exists
        if (!stockMap[stockData.productId]) {
          stockMap[stockData.productId] = stockData.quantity || 0;
        }
      });

      console.log('Stock Data:', {
        outletId: outletId,
        stockWithOutletId: stockSnap1.size,
        stockWithNullOutletId: stockSnap2.size,
        totalStockEntries: Object.keys(stockMap).length,
        sampleStockDocs: [...stockSnap1.docs.slice(0, 2), ...stockSnap2.docs.slice(0, 2)].map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      });
      
      if (stockSnap1.size === 0 && stockSnap2.size === 0) {
        console.warn('⚠️ No stock data found for this outlet. All products will show as "Out of Stock".');
        console.warn('💡 Stock data may exist for other outlets. Please ensure you have selected the correct outlet.');
      }

      // Calculate stock status for each product
      let inStockCount = 0;
      let lowStockCount = 0;
      let noStockCount = 0;
      let highStockCount = 0;
      
      const lowStockList = [];
      const outOfStockList = [];
      const highStockList = [];

      allProducts.forEach(product => {
        const quantity = stockMap[product.id] || 0;
        const minPacks = product.minPacks || 0;
        const maxPacks = product.maxPacks || 0;
        const unitsPerPack = product.unitsPerPack > 0 ? product.unitsPerPack : 1;
        
        const lowThreshold = minPacks * unitsPerPack;
        const highThreshold = maxPacks * unitsPerPack;

        const productWithStock = {
          ...product,
          currentStock: quantity,
          lowThreshold,
          highThreshold,
          unitsPerPack
        };

        // Categorize stock status
        if (quantity === 0) {
          // No stock at all
          noStockCount++;
          outOfStockList.push(productWithStock);
        } else if (lowThreshold > 0 && quantity < lowThreshold) {
          // Below minimum threshold (low stock)
          lowStockCount++;
          lowStockList.push(productWithStock);
        } else if (highThreshold > 0 && quantity > highThreshold) {
          // Above maximum threshold (overstock)
          highStockCount++;
          highStockList.push(productWithStock);
        } else {
          // Normal stock level (in stock)
          inStockCount++;
        }
      });
      
      // Update stock product lists
      setLowStockProducts(lowStockList);
      setOutOfStockProducts(outOfStockList);
      setHighStockProducts(highStockList);

      console.log('Stock Stats:', {
        inStock: inStockCount,
        lowStock: lowStockCount,
        noStock: noStockCount,
        highStock: highStockCount,
        totalProducts: allProducts.length,
        stockMapSize: Object.keys(stockMap).length,
        sampleStock: allProducts.slice(0, 3).map(p => ({
          productId: p.id,
          productName: p.name || p.title,
          quantity: stockMap[p.id] || 0,
          minPacks: p.minPacks,
          maxPacks: p.maxPacks,
          unitsPerPack: p.unitsPerPack,
          lowThreshold: (p.minPacks || 0) * (p.unitsPerPack > 0 ? p.unitsPerPack : 1),
          highThreshold: (p.maxPacks || 0) * (p.unitsPerPack > 0 ? p.unitsPerPack : 1)
        }))
      });

      // Calculate growth comparisons (same day last week, last month, last year)
      const currentDate = new Date(selectedDate);
      
      // Same day last week (7 days ago)
      const lastWeekDate = new Date(currentDate);
      lastWeekDate.setDate(lastWeekDate.getDate() - 7);
      const lastWeekDateStr = getLocalDateString(lastWeekDate);
      
      // Same day last month (approximately 30 days ago)
      const lastMonthDate = new Date(currentDate);
      lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
      const lastMonthDateStr = getLocalDateString(lastMonthDate);
      
      // Same day last year (365 days ago)
      const lastYearDate = new Date(currentDate);
      lastYearDate.setFullYear(lastYearDate.getFullYear() - 1);
      const lastYearDateStr = getLocalDateString(lastYearDate);
      
      // Calculate sales for comparison dates
      const lastWeekInvoices = allInvoices.filter(inv => {
        const invDate = inv.timestamp?.toDate?.() || new Date(inv.timestamp);
        const invDateStr = getLocalDateString(invDate);
        return invDateStr === lastWeekDateStr && inv.status === 'completed';
      });
      
      const lastMonthInvoices = allInvoices.filter(inv => {
        const invDate = inv.timestamp?.toDate?.() || new Date(inv.timestamp);
        const invDateStr = getLocalDateString(invDate);
        return invDateStr === lastMonthDateStr && inv.status === 'completed';
      });
      
      const lastYearInvoices = allInvoices.filter(inv => {
        const invDate = inv.timestamp?.toDate?.() || new Date(inv.timestamp);
        const invDateStr = getLocalDateString(invDate);
        return invDateStr === lastYearDateStr && inv.status === 'completed';
      });
      
      const lastWeekSales = lastWeekInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
      const lastMonthSales = lastMonthInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
      const lastYearSales = lastYearInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
      
      // Calculate growth percentages
      const lastWeekGrowth = lastWeekSales > 0 ? ((netSales - lastWeekSales) / lastWeekSales) * 100 : 0;
      const lastMonthGrowth = lastMonthSales > 0 ? ((netSales - lastMonthSales) / lastMonthSales) * 100 : 0;
      const lastYearGrowth = lastYearSales > 0 ? ((netSales - lastYearSales) / lastYearSales) * 100 : 0;
      
      console.log('Growth Comparisons:', {
        current: netSales,
        lastWeek: lastWeekSales,
        lastWeekGrowth: lastWeekGrowth.toFixed(2) + '%',
        lastMonth: lastMonthSales,
        lastMonthGrowth: lastMonthGrowth.toFixed(2) + '%',
        lastYear: lastYearSales,
        lastYearGrowth: lastYearGrowth.toFixed(2) + '%'
      });

      setStats({
        totalSales,
        grossSales,
        netSales,
        voidedSales,
        voidPercentage,
        returns,
        discounts,
        totalOrders: invoices.length,
        totalCustomers: totalUniqueCustomers,
        totalProducts: productsSnap.size,
        activeProducts,
        inactiveProducts,
        inStock: inStockCount,
        lowStock: lowStockCount,
        noStock: noStockCount,
        highStock: highStockCount,
        cashSales,
        cardSales,
        upiSales,
        otherPayments,
        highValueCustomers,
        mediumValueCustomers,
        lowValueCustomers,
        lastWeekSales,
        lastWeekGrowth,
        lastMonthSales,
        lastMonthGrowth,
        lastYearSales,
        lastYearGrowth
      });

      // Load recent invoices (all invoices for selected date, sorted by time)
      const recentOrdersData = invoices
        .sort((a, b) => {
          const dateA = a.timestamp?.toDate?.() || new Date(a.timestamp);
          const dateB = b.timestamp?.toDate?.() || new Date(b.timestamp);
          return dateB - dateA;
        });
      
      setRecentOrders(recentOrdersData);
      setCurrentPage(1); // Reset to first page when data changes

    } catch (error) {
      console.error('Error processing dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'warning',
      processing: 'info',
      completed: 'success',
      cancelled: 'danger'
    };
    return badges[status] || 'secondary';
  };

  // Get unique statuses from current data
  const availableStatuses = [...new Set(recentOrders.map(order => order.status).filter(Boolean))].sort();

  // Apply filters first
  const filteredOrders = recentOrders.filter(order => {
    // Status filter
    if (statusFilter !== 'all' && order.status !== statusFilter) {
      return false;
    }

    // Search filter (searches in ID, customer name, and employee name)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesId = order.id?.toLowerCase().includes(searchLower);
      const matchesCustomer = order.customerName?.toLowerCase().includes(searchLower);
      const matchesEmployee = order.employeeName?.toLowerCase().includes(searchLower);
      const matchesNumericId = order.numericId?.toString().includes(searchLower);
      
      if (!matchesId && !matchesCustomer && !matchesEmployee && !matchesNumericId) {
        return false;
      }
    }

    return true;
  });

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default desc direction
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Sort filtered orders based on current sort field and direction
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    let aValue, bValue;

    switch(sortField) {
      case 'id':
        aValue = a.id || '';
        bValue = b.id || '';
        break;
      case 'customerName':
        aValue = (a.customerName || 'Guest').toLowerCase();
        bValue = (b.customerName || 'Guest').toLowerCase();
        break;
      case 'timestamp':
        aValue = a.timestamp ? (typeof a.timestamp === 'string' ? new Date(a.timestamp) : a.timestamp.toDate?.() || a.timestamp) : new Date(0);
        bValue = b.timestamp ? (typeof b.timestamp === 'string' ? new Date(b.timestamp) : b.timestamp.toDate?.() || b.timestamp) : new Date(0);
        break;
      case 'totalAmount':
        aValue = a.totalAmount || 0;
        bValue = b.totalAmount || 0;
        break;
      case 'status':
        aValue = (a.status || 'pending').toLowerCase();
        bValue = (b.status || 'pending').toLowerCase();
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination calculations
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = sortedOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(sortedOrders.length / ordersPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle sorting for Low Stock table
  const handleLowStockSort = (field) => {
    if (lowStockSortField === field) {
      setLowStockSortDirection(lowStockSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setLowStockSortField(field);
      setLowStockSortDirection('asc');
    }
    setLowStockCurrentPage(1);
  };

  // Handle sorting for High Stock table
  const handleHighStockSort = (field) => {
    if (highStockSortField === field) {
      setHighStockSortDirection(highStockSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setHighStockSortField(field);
      setHighStockSortDirection('asc');
    }
    setHighStockCurrentPage(1);
  };

  // Fullscreen toggle function
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      // Enter fullscreen - hide layout elements
      const sidebar = document.querySelector('.sidebar');
      const navbar = document.querySelector('.navbar');
      if (sidebar) sidebar.style.display = 'none';
      if (navbar) navbar.style.display = 'none';
      
      // Make dashboard fullscreen
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error('Error attempting to enable fullscreen:', err);
        // Restore sidebar and navbar if fullscreen fails
        if (sidebar) sidebar.style.display = '';
        if (navbar) navbar.style.display = '';
      });
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => {
          setIsFullscreen(false);
          // Restore sidebar and navbar
          const sidebar = document.querySelector('.sidebar');
          const navbar = document.querySelector('.navbar');
          if (sidebar) sidebar.style.display = '';
          if (navbar) navbar.style.display = '';
        });
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isNowFullscreen);
      
      // If user exits fullscreen via ESC, restore sidebar and navbar
      if (!isNowFullscreen) {
        const sidebar = document.querySelector('.sidebar');
        const navbar = document.querySelector('.navbar');
        if (sidebar) sidebar.style.display = '';
        if (navbar) navbar.style.display = '';
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Update current date/time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format date and time
  const formatDateTime = (date) => {
    const options = {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    };
    return date.toLocaleString('en-US', options);
  };

  return (
    <div className={`container-fluid ${isFullscreen ? 'fullscreen-dashboard' : ''}`} style={isFullscreen ? {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: isDark ? '#1a1a1a' : '#f8f9fa',
      overflowY: 'auto',
      zIndex: 9999,
      padding: '2rem'
    } : {}}>
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-start w-100 position-relative">
          <div style={{ flex: '1' }}>
            <h1 className="page-title">Dashboard</h1>
            <p className="page-subtitle mb-0">Welcome to POS Admin - Today's Performance</p>
          </div>
          {isFullscreen && (
            <div className="position-absolute top-0 start-50 translate-middle-x text-center">
              <div className="fw-semibold text-primary" style={{ fontSize: '1.2rem', lineHeight: '1.2' }}>
                {formatDateTime(currentDateTime)}
              </div>
              <div className="small text-muted">Live</div>
            </div>
          )}
          <div style={{ flex: '1' }} className="d-flex justify-content-end">
            <button 
              className="btn btn-outline-primary d-flex align-items-center gap-2"
              onClick={toggleFullscreen}
              title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
              {isFullscreen ? (
                <>
                  <FiMinimize size={20} />
                  <span className="d-none d-md-inline">Exit Fullscreen</span>
                </>
              ) : (
                <>
                  <FiMaximize size={20} />
                  <span className="d-none d-md-inline">Fullscreen</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Sales Summary Section */}
      <div className="row mb-3">
        <div className="col-12">
          <h5 className="mb-0 fw-bold">Sales Summary</h5>
          <p className="text-muted small">Overview of sales performance</p>
        </div>
      </div>
      
      <div className="row g-4 mb-4">
        <div className="col-12 col-sm-6 col-lg-4">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#2196F3' }}>
                <FiTrendingUp className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.grossSales} decimals={2} />
              </div>
              <div className="stat-card-label">Gross Sales</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-4">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#ff6b6b' }}>
                <FiXCircle className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.voidedSales} decimals={2} />
              </div>
              <div className="stat-card-label">Void Sales</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-4">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#48bb78' }}>
                <FiTrendingUp className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.netSales} decimals={2} />
              </div>
              <div className="stat-card-label">Net Sales</div>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Growth Section */}
      <div className="row mb-3">
        <div className="col-12">
          <h5 className="mb-0 fw-bold">Sales Growth</h5>
          <p className="text-muted small">Comparison with same day in previous periods</p>
        </div>
      </div>
      
      <div className="row g-4 mb-4">
        <div className="col-12 col-sm-6 col-lg-4">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#667eea' }}>
                <FiCalendar className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.lastWeekSales} decimals={2} />
              </div>
              <div className="stat-card-label d-flex align-items-center justify-content-between">
                <span>Last Week</span>
                <span className={`small fw-bold ${stats.lastWeekGrowth >= 0 ? 'text-success' : 'text-danger'}`}>
                  {stats.lastWeekGrowth >= 0 ? '+' : ''}
                  <AnimatedCounter value={loading ? 0 : Math.abs(stats.lastWeekGrowth)} decimals={1} suffix="%" />
                  {stats.lastWeekGrowth >= 0 ? (
                    <FiArrowUp className="ms-1" size={14} />
                  ) : (
                    <FiArrowDown className="ms-1" size={14} />
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-4">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#f093fb' }}>
                <FiCalendar className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.lastMonthSales} decimals={2} />
              </div>
              <div className="stat-card-label d-flex align-items-center justify-content-between">
                <span>Last Month</span>
                <span className={`small fw-bold ${stats.lastMonthGrowth >= 0 ? 'text-success' : 'text-danger'}`}>
                  {stats.lastMonthGrowth >= 0 ? '+' : ''}
                  <AnimatedCounter value={loading ? 0 : Math.abs(stats.lastMonthGrowth)} decimals={1} suffix="%" />
                  {stats.lastMonthGrowth >= 0 ? (
                    <FiArrowUp className="ms-1" size={14} />
                  ) : (
                    <FiArrowDown className="ms-1" size={14} />
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-4">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#4facfe' }}>
                <FiCalendar className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.lastYearSales} decimals={2} />
              </div>
              <div className="stat-card-label d-flex align-items-center justify-content-between">
                <span>Last Year</span>
                <span className={`small fw-bold ${stats.lastYearGrowth >= 0 ? 'text-success' : 'text-danger'}`}>
                  {stats.lastYearGrowth >= 0 ? '+' : ''}
                  <AnimatedCounter value={loading ? 0 : Math.abs(stats.lastYearGrowth)} decimals={1} suffix="%" />
                  {stats.lastYearGrowth >= 0 ? (
                    <FiArrowUp className="ms-1" size={14} />
                  ) : (
                    <FiArrowDown className="ms-1" size={14} />
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Types Section */}
      <div className="row mb-3">
        <div className="col-12">
          <h5 className="mb-0 fw-bold">Payment Types</h5>
          <p className="text-muted small">Sales breakdown by payment method</p>
        </div>
      </div>
      
      <div className="row g-4 mb-4">
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#48bb78' }}>
                <FiDollarSign className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.cashSales} decimals={2} />
              </div>
              <div className="stat-card-label">Cash</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#4299e1' }}>
                <FiCreditCard className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.cardSales} decimals={2} />
              </div>
              <div className="stat-card-label">Card</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#9f7aea' }}>
                <FiSmartphone className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.upiSales} decimals={2} />
              </div>
              <div className="stat-card-label">UPI</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#a0aec0' }}>
                <FiTrendingUp className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.otherPayments} decimals={2} />
              </div>
              <div className="stat-card-label">Other</div>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Adjustments Section */}
      <div className="row mb-3">
        <div className="col-12">
          <h5 className="mb-0 fw-bold">Sales Adjustments</h5>
          <p className="text-muted small">Returns, voids, and discounts applied</p>
        </div>
      </div>
      
      <div className="row g-4 mb-4">
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#4299e1' }}>
                <FiRotateCcw className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.returns} decimals={2} />
              </div>
              <div className="stat-card-label">Returns</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#ff6b6b' }}>
                <FiXCircle className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.voidedSales} decimals={2} />
              </div>
              <div className="stat-card-label">Voids</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#ed8936' }}>
                <FiTag className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.discounts} decimals={2} />
              </div>
              <div className="stat-card-label">Discounts</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#2196F3' }}>
                <FiFileText className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.totalOrders} />
              </div>
              <div className="stat-card-label">Total Invoices</div>
            </div>
          </div>
        </div>
      </div>

      {/* Products Section */}
      <div className="row mb-3">
        <div className="col-12">
          <h5 className="mb-0 fw-bold">Products</h5>
          <p className="text-muted small">Inventory overview</p>
        </div>
      </div>
      
      <div className="row g-4 mb-4">
        <div className="col-12 col-sm-6 col-lg-4">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: 'var(--accent-gradient)' }}>
                <FiPackage className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.totalProducts} />
              </div>
              <div className="stat-card-label">Total Products</div>
            </div>
          </div>
        </div>
        
        <div className="col-12 col-sm-6 col-lg-4">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#48bb78' }}>
                <FiPackage className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.activeProducts} />
              </div>
              <div className="stat-card-label">Active Products</div>
            </div>
          </div>
        </div>
        
        <div className="col-12 col-sm-6 col-lg-4">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#a0aec0' }}>
                <FiPackage className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.inactiveProducts} />
              </div>
              <div className="stat-card-label">Inactive Products</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stock Status Section */}
      <div className="row mb-3">
        <div className="col-12">
          <h5 className="mb-0 fw-bold">Stock Status</h5>
          <p className="text-muted small">Current inventory levels</p>
        </div>
      </div>
      
      <div className="row g-4 mb-4">
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#48bb78' }}>
                <FiPackage className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.inStock} />
              </div>
              <div className="stat-card-label">In Stock</div>
            </div>
          </div>
        </div>
        
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#f6ad55' }}>
                <FiPackage className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.lowStock} />
              </div>
              <div className="stat-card-label">Low Stock</div>
            </div>
          </div>
        </div>
        
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#fc8181' }}>
                <FiPackage className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.noStock} />
              </div>
              <div className="stat-card-label">Out of Stock</div>
            </div>
          </div>
        </div>
        
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#4299e1' }}>
                <FiPackage className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.highStock} />
              </div>
              <div className="stat-card-label">High Stock</div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Segmentation Section */}
      <div className="row mb-3">
        <div className="col-12">
          <h5 className="mb-0 fw-bold">Customer Segmentation</h5>
          <p className="text-muted small">Customers grouped by spending levels</p>
        </div>
      </div>
      
      <div className="row g-4 mb-4">
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#4299e1' }}>
                <FiUsers className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.totalCustomers} />
              </div>
              <div className="stat-card-label">
                Total Customers
                <small className="d-block text-muted" style={{ fontSize: '0.7rem', marginTop: '4px' }}>All customers</small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#48bb78' }}>
                <FiAward className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.highValueCustomers} />
              </div>
              <div className="stat-card-label">
                Top Customers
                <small className="d-block text-muted" style={{ fontSize: '0.7rem', marginTop: '4px' }}>&gt; 100 spent</small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#ecc94b' }}>
                <FiStar className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.mediumValueCustomers} />
              </div>
              <div className="stat-card-label">
                Core Customers
                <small className="d-block text-muted" style={{ fontSize: '0.7rem', marginTop: '4px' }}>50-99 spent</small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#fc8181' }}>
                <FiUserCheck className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.lowValueCustomers} />
              </div>
              <div className="stat-card-label">
                Occasional Buyers
                <small className="d-block text-muted" style={{ fontSize: '0.7rem', marginTop: '4px' }}>1-49 spent</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="row g-4">
        <div className="col-12">
          <div className="card hover-lift">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Today's Invoices</h5>
              <span className="badge bg-primary rounded-pill">{recentOrders.length}</span>
            </div>
            
            {/* Filter Controls */}
            {!loading && recentOrders.length > 0 && (
              <div className="card-body border-bottom">
                <div className="row g-3 align-items-end">
                  <div className="col-md-6">
                    <label className="form-label small text-muted mb-1">Search</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search by ID, customer, or employee..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small text-muted mb-1">Status</label>
                    <select
                      className="form-select form-select-sm"
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="all">All Status</option>
                      {availableStatuses.map(status => (
                        <option key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <button
                      className="btn btn-sm btn-outline-secondary w-100"
                      onClick={() => {
                        setSearchTerm('');
                        setStatusFilter('all');
                        setCurrentPage(1);
                      }}
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      Clear Filters
                    </button>
                  </div>
                </div>
                {(searchTerm || statusFilter !== 'all') && (
                  <div className="mt-2">
                    <small className="text-muted">
                      Showing {sortedOrders.length} of {recentOrders.length} invoices
                    </small>
                  </div>
                )}
              </div>
            )}
            
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mt-2">Loading invoices...</p>
                </div>
              ) : recentOrders.length === 0 ? (
                <div className="text-center py-5">
                  <FiShoppingCart size={48} className="text-muted mb-3" />
                  <p className="text-muted mb-0">No invoices to display.</p>
                  <small className="text-muted">Invoices will appear here once sales are made.</small>
                </div>
              ) : sortedOrders.length === 0 ? (
                <div className="text-center py-5">
                  <FiShoppingCart size={48} className="text-muted mb-3" />
                  <p className="text-muted mb-0">No invoices match your filters.</p>
                  <button
                    className="btn btn-sm btn-outline-primary mt-2"
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('all');
                      setCurrentPage(1);
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead>
                      <tr>
                        <th 
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('id')}
                        >
                          Invoice ID
                          {sortField === 'id' && (
                            sortDirection === 'asc' ? <FiArrowUp className="ms-1" size={14} /> : <FiArrowDown className="ms-1" size={14} />
                          )}
                        </th>
                        <th 
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('customerName')}
                        >
                          Customer
                          {sortField === 'customerName' && (
                            sortDirection === 'asc' ? <FiArrowUp className="ms-1" size={14} /> : <FiArrowDown className="ms-1" size={14} />
                          )}
                        </th>
                        <th 
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('timestamp')}
                        >
                          Date
                          {sortField === 'timestamp' && (
                            sortDirection === 'asc' ? <FiArrowUp className="ms-1" size={14} /> : <FiArrowDown className="ms-1" size={14} />
                          )}
                        </th>
                        <th 
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('totalAmount')}
                        >
                          Total
                          {sortField === 'totalAmount' && (
                            sortDirection === 'asc' ? <FiArrowUp className="ms-1" size={14} /> : <FiArrowDown className="ms-1" size={14} />
                          )}
                        </th>
                        <th 
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('status')}
                        >
                          Status
                          {sortField === 'status' && (
                            sortDirection === 'asc' ? <FiArrowUp className="ms-1" size={14} /> : <FiArrowDown className="ms-1" size={14} />
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentOrders.map((order) => (
                        <tr key={order.id}>
                          <td>
                            <span className="fw-semibold">
                              {order.invoiceNumber || order.numericId || `#${order.id.substring(0, 8)}`}
                            </span>
                          </td>
                          <td>{order.customerName || 'Guest'}</td>
                          <td>
                            <small className="text-muted">
                              <FiClock size={14} className="me-1" />
                              {formatDate(order.timestamp)}
                            </small>
                          </td>
                          <td>
                            <span className="fw-semibold">
                              {formatCurrency(order.totalAmount || 0)}
                            </span>
                          </td>
                          <td>
                            <span className={`badge bg-${getStatusBadge(order.status)}`}>
                              {order.status || 'pending'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Pagination Controls */}
              {!loading && sortedOrders.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3 px-3 flex-wrap gap-2">
                  <div className="text-muted small">
                    Showing {indexOfFirstOrder + 1} to {Math.min(indexOfLastOrder, sortedOrders.length)} of {sortedOrders.length} invoices
                  </div>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link"
                          style={{
                            backgroundColor: isDark ? '#2d2d2d' : '#fff',
                            borderColor: isDark ? '#444' : '#dee2e6',
                            color: isDark ? '#fff' : '#000'
                          }}
                          onClick={() => handlePageChange(1)}
                          disabled={currentPage === 1}
                        >
                          First
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link"
                          style={{
                            backgroundColor: isDark ? '#2d2d2d' : '#fff',
                            borderColor: isDark ? '#444' : '#dee2e6',
                            color: isDark ? '#fff' : '#000'
                          }}
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      
                      {/* Show smart pagination with ellipsis */}
                      {(() => {
                        const pages = [];
                        const maxPagesToShow = 5;
                        
                        if (totalPages <= maxPagesToShow + 2) {
                          // Show all pages if total is small
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(
                              <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                <button 
                                  className="page-link"
                                  style={{
                                    backgroundColor: currentPage === i 
                                      ? '#0d6efd' 
                                      : (isDark ? '#2d2d2d' : '#fff'),
                                    borderColor: isDark ? '#444' : '#dee2e6',
                                    color: currentPage === i ? '#fff' : (isDark ? '#fff' : '#000')
                                  }}
                                  onClick={() => handlePageChange(i)}
                                >
                                  {i}
                                </button>
                              </li>
                            );
                          }
                        } else {
                          // Always show first page
                          pages.push(
                            <li key={1} className={`page-item ${currentPage === 1 ? 'active' : ''}`}>
                              <button 
                                className="page-link"
                                style={{
                                  backgroundColor: currentPage === 1 
                                    ? '#0d6efd' 
                                    : (isDark ? '#2d2d2d' : '#fff'),
                                  borderColor: isDark ? '#444' : '#dee2e6',
                                  color: currentPage === 1 ? '#fff' : (isDark ? '#fff' : '#000')
                                }}
                                onClick={() => handlePageChange(1)}
                              >
                                1
                              </button>
                            </li>
                          );
                          
                          // Show ellipsis if needed
                          if (currentPage > 3) {
                            pages.push(
                              <li key="ellipsis1" className="page-item disabled">
                                <span 
                                  className="page-link"
                                  style={{
                                    backgroundColor: isDark ? '#2d2d2d' : '#fff',
                                    borderColor: isDark ? '#444' : '#dee2e6',
                                    color: isDark ? '#fff' : '#000'
                                  }}
                                >
                                  ...
                                </span>
                              </li>
                            );
                          }
                          
                          // Show pages around current page
                          const startPage = Math.max(2, currentPage - 1);
                          const endPage = Math.min(totalPages - 1, currentPage + 1);
                          
                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                <button 
                                  className="page-link"
                                  style={{
                                    backgroundColor: currentPage === i 
                                      ? '#0d6efd' 
                                      : (isDark ? '#2d2d2d' : '#fff'),
                                    borderColor: isDark ? '#444' : '#dee2e6',
                                    color: currentPage === i ? '#fff' : (isDark ? '#fff' : '#000')
                                  }}
                                  onClick={() => handlePageChange(i)}
                                >
                                  {i}
                                </button>
                              </li>
                            );
                          }
                          
                          // Show ellipsis if needed
                          if (currentPage < totalPages - 2) {
                            pages.push(
                              <li key="ellipsis2" className="page-item disabled">
                                <span 
                                  className="page-link"
                                  style={{
                                    backgroundColor: isDark ? '#2d2d2d' : '#fff',
                                    borderColor: isDark ? '#444' : '#dee2e6',
                                    color: isDark ? '#fff' : '#000'
                                  }}
                                >
                                  ...
                                </span>
                              </li>
                            );
                          }
                          
                          // Always show last page
                          pages.push(
                            <li key={totalPages} className={`page-item ${currentPage === totalPages ? 'active' : ''}`}>
                              <button 
                                className="page-link"
                                style={{
                                  backgroundColor: currentPage === totalPages 
                                    ? '#0d6efd' 
                                    : (isDark ? '#2d2d2d' : '#fff'),
                                  borderColor: isDark ? '#444' : '#dee2e6',
                                  color: currentPage === totalPages ? '#fff' : (isDark ? '#fff' : '#000')
                                }}
                                onClick={() => handlePageChange(totalPages)}
                              >
                                {totalPages}
                              </button>
                            </li>
                          );
                        }
                        
                        return pages;
                      })()}
                      
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link"
                          style={{
                            backgroundColor: isDark ? '#2d2d2d' : '#fff',
                            borderColor: isDark ? '#444' : '#dee2e6',
                            color: isDark ? '#fff' : '#000'
                          }}
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link"
                          style={{
                            backgroundColor: isDark ? '#2d2d2d' : '#fff',
                            borderColor: isDark ? '#444' : '#dee2e6',
                            color: isDark ? '#fff' : '#000'
                          }}
                          onClick={() => handlePageChange(totalPages)}
                          disabled={currentPage === totalPages}
                        >
                          Last
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stock Information Sections */}
      <div className="row g-4 mt-2">
        {/* Low Stock and Out of Stock Products */}
        <div className="col-12">
          <div className="card hover-lift">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Low Stock & Out of Stock Products</h5>
              <span className="badge bg-warning rounded-pill">
                {lowStockProducts.length + outOfStockProducts.length}
              </span>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mt-2">Loading stock information...</p>
                </div>
              ) : (lowStockProducts.length === 0 && outOfStockProducts.length === 0) ? (
                <div className="text-center py-5">
                  <FiPackage size={48} className="text-muted mb-3" />
                  <p className="text-muted mb-0">No low stock or out of stock products.</p>
                  <small className="text-muted">All products have adequate stock levels.</small>
                </div>
              ) : (() => {
                // Combine low/out of stock products
                const combinedLowStock = [...outOfStockProducts, ...lowStockProducts];
                
                // Apply filters and search
                const filteredLowStock = combinedLowStock.filter(product => {
                  // Status filter
                  if (lowStockStatusFilter === 'out-of-stock' && product.currentStock !== 0) return false;
                  if (lowStockStatusFilter === 'low-stock' && product.currentStock === 0) return false;
                  
                  // Category filter
                  if (lowStockCategoryFilter !== 'all' && product.category !== lowStockCategoryFilter) return false;
                  
                  // Search filter
                  if (lowStockSearchTerm) {
                    const searchLower = lowStockSearchTerm.toLowerCase();
                    const name = (product.name || product.title || '').toLowerCase();
                    return name.includes(searchLower);
                  }
                  
                  return true;
                });
                
                // Apply sorting
                const sortedLowStock = [...filteredLowStock].sort((a, b) => {
                  let aValue, bValue;
                  
                  switch(lowStockSortField) {
                    case 'name':
                      aValue = (a.name || a.title || '').toLowerCase();
                      bValue = (b.name || b.title || '').toLowerCase();
                      break;
                    case 'category':
                      aValue = (a.category || 'Uncategorized').toLowerCase();
                      bValue = (b.category || 'Uncategorized').toLowerCase();
                      break;
                    case 'currentStock':
                      aValue = a.currentStock || 0;
                      bValue = b.currentStock || 0;
                      break;
                    case 'lowThreshold':
                      aValue = a.lowThreshold || 0;
                      bValue = b.lowThreshold || 0;
                      break;
                    default:
                      return 0;
                  }
                  
                  if (aValue < bValue) return lowStockSortDirection === 'asc' ? -1 : 1;
                  if (aValue > bValue) return lowStockSortDirection === 'asc' ? 1 : -1;
                  return 0;
                });
                
                // Pagination
                const totalLowStockPages = Math.ceil(sortedLowStock.length / stockItemsPerPage);
                const indexOfLastLowStock = lowStockCurrentPage * stockItemsPerPage;
                const indexOfFirstLowStock = indexOfLastLowStock - stockItemsPerPage;
                const currentLowStockItems = sortedLowStock.slice(indexOfFirstLowStock, indexOfLastLowStock);

                return (
                  <>
                    {/* Filter Controls */}
                    {combinedLowStock.length > 0 && (
                      <div className="border-bottom pb-3 mb-3">
                        <div className="row g-3 align-items-end">
                          <div className="col-md-5">
                            <label className="form-label small text-muted mb-1">Search</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Search by product name..."
                              value={lowStockSearchTerm}
                              onChange={(e) => {
                                setLowStockSearchTerm(e.target.value);
                                setLowStockCurrentPage(1);
                              }}
                            />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label small text-muted mb-1">Status</label>
                            <select
                              className="form-select form-select-sm"
                              value={lowStockStatusFilter}
                              onChange={(e) => {
                                setLowStockStatusFilter(e.target.value);
                                setLowStockCurrentPage(1);
                              }}
                            >
                              <option value="all">All Status</option>
                              <option value="out-of-stock">Out of Stock</option>
                              <option value="low-stock">Low Stock</option>
                            </select>
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small text-muted mb-1">Category</label>
                            <select
                              className="form-select form-select-sm"
                              value={lowStockCategoryFilter}
                              onChange={(e) => {
                                setLowStockCategoryFilter(e.target.value);
                                setLowStockCurrentPage(1);
                              }}
                            >
                              <option value="all">All Categories</option>
                              {categories.map(cat => (
                                <option key={cat.id} value={cat.name}>{cat.name}</option>
                              ))}
                              <option value="Uncategorized">Uncategorized</option>
                            </select>
                          </div>
                          <div className="col-md-2">
                            <button
                              className="btn btn-sm btn-outline-secondary w-100"
                              onClick={() => {
                                setLowStockSearchTerm('');
                                setLowStockStatusFilter('all');
                                setLowStockCategoryFilter('all');
                                setLowStockCurrentPage(1);
                              }}
                            >
                              <i className="bi bi-x-circle me-1"></i>
                              Clear
                            </button>
                          </div>
                        </div>
                        {(lowStockSearchTerm || lowStockStatusFilter !== 'all' || lowStockCategoryFilter !== 'all') && (
                          <div className="mt-2">
                            <small className="text-muted">
                              Showing {filteredLowStock.length} of {combinedLowStock.length} products
                            </small>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {filteredLowStock.length === 0 ? (
                      <div className="text-center py-5">
                        <FiPackage size={48} className="text-muted mb-3" />
                        <p className="text-muted mb-0">No products match your filters.</p>
                        <button
                          className="btn btn-sm btn-outline-primary mt-2"
                          onClick={() => {
                            setLowStockSearchTerm('');
                            setLowStockStatusFilter('all');
                            setLowStockCategoryFilter('all');
                            setLowStockCurrentPage(1);
                          }}
                        >
                          Clear Filters
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="table-responsive">
                          <table className="table table-hover align-middle">
                            <thead>
                              <tr>
                                <th 
                                  style={{ cursor: 'pointer', userSelect: 'none' }}
                                  onClick={() => handleLowStockSort('name')}
                                >
                                  Product Name{' '}
                                  {lowStockSortField === 'name' && (
                                    lowStockSortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />
                                  )}
                                </th>
                                <th 
                                  style={{ cursor: 'pointer', userSelect: 'none' }}
                                  onClick={() => handleLowStockSort('category')}
                                >
                                  Category{' '}
                                  {lowStockSortField === 'category' && (
                                    lowStockSortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />
                                  )}
                                </th>
                                <th 
                                  style={{ cursor: 'pointer', userSelect: 'none' }}
                                  onClick={() => handleLowStockSort('currentStock')}
                                >
                                  Current Stock{' '}
                                  {lowStockSortField === 'currentStock' && (
                                    lowStockSortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />
                                  )}
                                </th>
                                <th 
                                  style={{ cursor: 'pointer', userSelect: 'none' }}
                                  onClick={() => handleLowStockSort('lowThreshold')}
                                >
                                  Min Threshold{' '}
                                  {lowStockSortField === 'lowThreshold' && (
                                    lowStockSortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />
                                  )}
                                </th>
                                <th>Status</th>
                              </tr>
                            </thead>
                        <tbody>
                          {currentLowStockItems.map((product) => (
                            <tr key={product.id}>
                              <td>
                                <span className="fw-semibold">{product.name || product.title}</span>
                              </td>
                              <td>
                                <small className="text-muted">{product.category || 'Uncategorized'}</small>
                              </td>
                              <td>
                                <span className={`fw-bold ${product.currentStock === 0 ? 'text-danger' : 'text-warning'}`}>
                                  {product.currentStock} {product.unit || 'units'}
                                </span>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {product.lowThreshold} {product.unit || 'units'}
                                </small>
                              </td>
                              <td>
                                <span className={`badge ${product.currentStock === 0 ? 'bg-danger' : 'bg-warning'}`}>
                                  {product.currentStock === 0 ? 'Out of Stock' : 'Low Stock'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination for Low Stock */}
                    {totalLowStockPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3 px-3 flex-wrap gap-2">
                        <div className="text-muted small">
                          Showing {indexOfFirstLowStock + 1} to {Math.min(indexOfLastLowStock, filteredLowStock.length)} of {filteredLowStock.length} products
                        </div>
                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${lowStockCurrentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link"
                                style={{
                                  backgroundColor: isDark ? '#2d2d2d' : '#fff',
                                  borderColor: isDark ? '#444' : '#dee2e6',
                                  color: isDark ? '#fff' : '#000'
                                }}
                                onClick={() => setLowStockCurrentPage(1)}
                                disabled={lowStockCurrentPage === 1}
                              >
                                First
                              </button>
                            </li>
                            <li className={`page-item ${lowStockCurrentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link"
                                style={{
                                  backgroundColor: isDark ? '#2d2d2d' : '#fff',
                                  borderColor: isDark ? '#444' : '#dee2e6',
                                  color: isDark ? '#fff' : '#000'
                                }}
                                onClick={() => setLowStockCurrentPage(lowStockCurrentPage - 1)}
                                disabled={lowStockCurrentPage === 1}
                              >
                                Previous
                              </button>
                            </li>
                            
                            {(() => {
                              const pages = [];
                              const maxPagesToShow = 5;
                              
                              if (totalLowStockPages <= maxPagesToShow + 2) {
                                for (let i = 1; i <= totalLowStockPages; i++) {
                                  pages.push(
                                    <li key={i} className={`page-item ${lowStockCurrentPage === i ? 'active' : ''}`}>
                                      <button 
                                        className="page-link"
                                        style={{
                                          backgroundColor: lowStockCurrentPage === i 
                                            ? '#0d6efd' 
                                            : (isDark ? '#2d2d2d' : '#fff'),
                                          borderColor: isDark ? '#444' : '#dee2e6',
                                          color: lowStockCurrentPage === i ? '#fff' : (isDark ? '#fff' : '#000')
                                        }}
                                        onClick={() => setLowStockCurrentPage(i)}
                                      >
                                        {i}
                                      </button>
                                    </li>
                                  );
                                }
                              } else {
                                pages.push(
                                  <li key={1} className={`page-item ${lowStockCurrentPage === 1 ? 'active' : ''}`}>
                                    <button 
                                      className="page-link"
                                      style={{
                                        backgroundColor: lowStockCurrentPage === 1 
                                          ? '#0d6efd' 
                                          : (isDark ? '#2d2d2d' : '#fff'),
                                        borderColor: isDark ? '#444' : '#dee2e6',
                                        color: lowStockCurrentPage === 1 ? '#fff' : (isDark ? '#fff' : '#000')
                                      }}
                                      onClick={() => setLowStockCurrentPage(1)}
                                    >
                                      1
                                    </button>
                                  </li>
                                );
                                
                                if (lowStockCurrentPage > 3) {
                                  pages.push(
                                    <li key="ellipsis1" className="page-item disabled">
                                      <span 
                                        className="page-link"
                                        style={{
                                          backgroundColor: isDark ? '#2d2d2d' : '#fff',
                                          borderColor: isDark ? '#444' : '#dee2e6',
                                          color: isDark ? '#fff' : '#000'
                                        }}
                                      >
                                        ...
                                      </span>
                                    </li>
                                  );
                                }
                                
                                const startPage = Math.max(2, lowStockCurrentPage - 1);
                                const endPage = Math.min(totalLowStockPages - 1, lowStockCurrentPage + 1);
                                
                                for (let i = startPage; i <= endPage; i++) {
                                  pages.push(
                                    <li key={i} className={`page-item ${lowStockCurrentPage === i ? 'active' : ''}`}>
                                      <button 
                                        className="page-link"
                                        style={{
                                          backgroundColor: lowStockCurrentPage === i 
                                            ? '#0d6efd' 
                                            : (isDark ? '#2d2d2d' : '#fff'),
                                          borderColor: isDark ? '#444' : '#dee2e6',
                                          color: lowStockCurrentPage === i ? '#fff' : (isDark ? '#fff' : '#000')
                                        }}
                                        onClick={() => setLowStockCurrentPage(i)}
                                      >
                                        {i}
                                      </button>
                                    </li>
                                  );
                                }
                                
                                if (lowStockCurrentPage < totalLowStockPages - 2) {
                                  pages.push(
                                    <li key="ellipsis2" className="page-item disabled">
                                      <span 
                                        className="page-link"
                                        style={{
                                          backgroundColor: isDark ? '#2d2d2d' : '#fff',
                                          borderColor: isDark ? '#444' : '#dee2e6',
                                          color: isDark ? '#fff' : '#000'
                                        }}
                                      >
                                        ...
                                      </span>
                                    </li>
                                  );
                                }
                                
                                pages.push(
                                  <li key={totalLowStockPages} className={`page-item ${lowStockCurrentPage === totalLowStockPages ? 'active' : ''}`}>
                                    <button 
                                      className="page-link"
                                      style={{
                                        backgroundColor: lowStockCurrentPage === totalLowStockPages 
                                          ? '#0d6efd' 
                                          : (isDark ? '#2d2d2d' : '#fff'),
                                        borderColor: isDark ? '#444' : '#dee2e6',
                                        color: lowStockCurrentPage === totalLowStockPages ? '#fff' : (isDark ? '#fff' : '#000')
                                      }}
                                      onClick={() => setLowStockCurrentPage(totalLowStockPages)}
                                    >
                                      {totalLowStockPages}
                                    </button>
                                  </li>
                                );
                              }
                              
                              return pages;
                            })()}
                            
                            <li className={`page-item ${lowStockCurrentPage === totalLowStockPages ? 'disabled' : ''}`}>
                              <button 
                                className="page-link"
                                style={{
                                  backgroundColor: isDark ? '#2d2d2d' : '#fff',
                                  borderColor: isDark ? '#444' : '#dee2e6',
                                  color: isDark ? '#fff' : '#000'
                                }}
                                onClick={() => setLowStockCurrentPage(lowStockCurrentPage + 1)}
                                disabled={lowStockCurrentPage === totalLowStockPages}
                              >
                                Next
                              </button>
                            </li>
                            <li className={`page-item ${lowStockCurrentPage === totalLowStockPages ? 'disabled' : ''}`}>
                              <button 
                                className="page-link"
                                style={{
                                  backgroundColor: isDark ? '#2d2d2d' : '#fff',
                                  borderColor: isDark ? '#444' : '#dee2e6',
                                  color: isDark ? '#fff' : '#000'
                                }}
                                onClick={() => setLowStockCurrentPage(totalLowStockPages)}
                                disabled={lowStockCurrentPage === totalLowStockPages}
                              >
                                Last
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* High Stock Products */}
        <div className="col-12">
          <div className="card hover-lift">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">High Stock Products</h5>
              <span className="badge bg-info rounded-pill">{highStockProducts.length}</span>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mt-2">Loading stock information...</p>
                </div>
              ) : highStockProducts.length === 0 ? (
                <div className="text-center py-5">
                  <FiPackage size={48} className="text-muted mb-3" />
                  <p className="text-muted mb-0">No overstocked products.</p>
                  <small className="text-muted">All products are within normal stock levels.</small>
                </div>
              ) : (() => {
                // Apply search and category filter
                const filteredHighStock = highStockProducts.filter(product => {
                  // Category filter
                  if (highStockCategoryFilter !== 'all' && product.category !== highStockCategoryFilter) return false;
                  
                  // Search filter
                  if (highStockSearchTerm) {
                    const searchLower = highStockSearchTerm.toLowerCase();
                    const name = (product.name || product.title || '').toLowerCase();
                    return name.includes(searchLower);
                  }
                  return true;
                });
                
                // Apply sorting
                const sortedHighStock = [...filteredHighStock].sort((a, b) => {
                  let aValue, bValue;
                  
                  switch(highStockSortField) {
                    case 'name':
                      aValue = (a.name || a.title || '').toLowerCase();
                      bValue = (b.name || b.title || '').toLowerCase();
                      break;
                    case 'category':
                      aValue = (a.category || 'Uncategorized').toLowerCase();
                      bValue = (b.category || 'Uncategorized').toLowerCase();
                      break;
                    case 'currentStock':
                      aValue = a.currentStock || 0;
                      bValue = b.currentStock || 0;
                      break;
                    case 'highThreshold':
                      aValue = a.highThreshold || 0;
                      bValue = b.highThreshold || 0;
                      break;
                    default:
                      return 0;
                  }
                  
                  if (aValue < bValue) return highStockSortDirection === 'asc' ? -1 : 1;
                  if (aValue > bValue) return highStockSortDirection === 'asc' ? 1 : -1;
                  return 0;
                });
                
                // Pagination
                const totalHighStockPages = Math.ceil(sortedHighStock.length / stockItemsPerPage);
                const indexOfLastHighStock = highStockCurrentPage * stockItemsPerPage;
                const indexOfFirstHighStock = indexOfLastHighStock - stockItemsPerPage;
                const currentHighStockItems = sortedHighStock.slice(indexOfFirstHighStock, indexOfLastHighStock);

                return (
                  <>
                    {/* Filter Controls */}
                    {highStockProducts.length > 0 && (
                      <div className="border-bottom pb-3 mb-3">
                        <div className="row g-3 align-items-end">
                          <div className="col-md-7">
                            <label className="form-label small text-muted mb-1">Search</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Search by product name..."
                              value={highStockSearchTerm}
                              onChange={(e) => {
                                setHighStockSearchTerm(e.target.value);
                                setHighStockCurrentPage(1);
                              }}
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small text-muted mb-1">Category</label>
                            <select
                              className="form-select form-select-sm"
                              value={highStockCategoryFilter}
                              onChange={(e) => {
                                setHighStockCategoryFilter(e.target.value);
                                setHighStockCurrentPage(1);
                              }}
                            >
                              <option value="all">All Categories</option>
                              {categories.map(cat => (
                                <option key={cat.id} value={cat.name}>{cat.name}</option>
                              ))}
                              <option value="Uncategorized">Uncategorized</option>
                            </select>
                          </div>
                          <div className="col-md-2">
                            <button
                              className="btn btn-sm btn-outline-secondary w-100"
                              onClick={() => {
                                setHighStockSearchTerm('');
                                setHighStockCategoryFilter('all');
                                setHighStockCurrentPage(1);
                              }}
                            >
                              <i className="bi bi-x-circle me-1"></i>
                              Clear
                            </button>
                          </div>
                        </div>
                        {(highStockSearchTerm || highStockCategoryFilter !== 'all') && (
                          <div className="mt-2">
                            <small className="text-muted">
                              Showing {filteredHighStock.length} of {highStockProducts.length} products
                            </small>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {filteredHighStock.length === 0 ? (
                      <div className="text-center py-5">
                        <FiPackage size={48} className="text-muted mb-3" />
                        <p className="text-muted mb-0">No products match your filters.</p>
                        <button
                          className="btn btn-sm btn-outline-primary mt-2"
                          onClick={() => {
                            setHighStockSearchTerm('');
                            setHighStockCategoryFilter('all');
                            setHighStockCurrentPage(1);
                          }}
                        >
                          Clear Filters
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="table-responsive">
                          <table className="table table-hover align-middle">
                            <thead>
                              <tr>
                                <th 
                                  style={{ cursor: 'pointer', userSelect: 'none' }}
                                  onClick={() => handleHighStockSort('name')}
                                >
                                  Product Name{' '}
                                  {highStockSortField === 'name' && (
                                    highStockSortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />
                                  )}
                                </th>
                                <th 
                                  style={{ cursor: 'pointer', userSelect: 'none' }}
                                  onClick={() => handleHighStockSort('category')}
                                >
                                  Category{' '}
                                  {highStockSortField === 'category' && (
                                    highStockSortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />
                                  )}
                                </th>
                                <th 
                                  style={{ cursor: 'pointer', userSelect: 'none' }}
                                  onClick={() => handleHighStockSort('currentStock')}
                                >
                                  Current Stock{' '}
                                  {highStockSortField === 'currentStock' && (
                                    highStockSortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />
                                  )}
                                </th>
                                <th 
                                  style={{ cursor: 'pointer', userSelect: 'none' }}
                                  onClick={() => handleHighStockSort('highThreshold')}
                                >
                                  Max Threshold{' '}
                                  {highStockSortField === 'highThreshold' && (
                                    highStockSortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />
                                  )}
                                </th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {currentHighStockItems.map((product) => (
                                <tr key={product.id}>
                                  <td>
                                    <span className="fw-semibold">{product.name || product.title}</span>
                              </td>
                              <td>
                                <small className="text-muted">{product.category || 'Uncategorized'}</small>
                              </td>
                              <td>
                                <span className="text-info fw-bold">
                                  {product.currentStock} {product.unit || 'units'}
                                </span>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {product.highThreshold} {product.unit || 'units'}
                                </small>
                              </td>
                              <td>
                                <span className="badge bg-info">High Stock</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination for High Stock */}
                    {totalHighStockPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3 px-3 flex-wrap gap-2">
                        <div className="text-muted small">
                          Showing {indexOfFirstHighStock + 1} to {Math.min(indexOfLastHighStock, filteredHighStock.length)} of {filteredHighStock.length} products
                        </div>
                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${highStockCurrentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link"
                                style={{
                                  backgroundColor: isDark ? '#2d2d2d' : '#fff',
                                  borderColor: isDark ? '#444' : '#dee2e6',
                                  color: isDark ? '#fff' : '#000'
                                }}
                                onClick={() => setHighStockCurrentPage(1)}
                                disabled={highStockCurrentPage === 1}
                              >
                                First
                              </button>
                            </li>
                            <li className={`page-item ${highStockCurrentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link"
                                style={{
                                  backgroundColor: isDark ? '#2d2d2d' : '#fff',
                                  borderColor: isDark ? '#444' : '#dee2e6',
                                  color: isDark ? '#fff' : '#000'
                                }}
                                onClick={() => setHighStockCurrentPage(highStockCurrentPage - 1)}
                                disabled={highStockCurrentPage === 1}
                              >
                                Previous
                              </button>
                            </li>
                            
                            {(() => {
                              const pages = [];
                              const maxPagesToShow = 5;
                              
                              if (totalHighStockPages <= maxPagesToShow + 2) {
                                for (let i = 1; i <= totalHighStockPages; i++) {
                                  pages.push(
                                    <li key={i} className={`page-item ${highStockCurrentPage === i ? 'active' : ''}`}>
                                      <button 
                                        className="page-link"
                                        style={{
                                          backgroundColor: highStockCurrentPage === i 
                                            ? '#0d6efd' 
                                            : (isDark ? '#2d2d2d' : '#fff'),
                                          borderColor: isDark ? '#444' : '#dee2e6',
                                          color: highStockCurrentPage === i ? '#fff' : (isDark ? '#fff' : '#000')
                                        }}
                                        onClick={() => setHighStockCurrentPage(i)}
                                      >
                                        {i}
                                      </button>
                                    </li>
                                  );
                                }
                              } else {
                                pages.push(
                                  <li key={1} className={`page-item ${highStockCurrentPage === 1 ? 'active' : ''}`}>
                                    <button 
                                      className="page-link"
                                      style={{
                                        backgroundColor: highStockCurrentPage === 1 
                                          ? '#0d6efd' 
                                          : (isDark ? '#2d2d2d' : '#fff'),
                                        borderColor: isDark ? '#444' : '#dee2e6',
                                        color: highStockCurrentPage === 1 ? '#fff' : (isDark ? '#fff' : '#000')
                                      }}
                                      onClick={() => setHighStockCurrentPage(1)}
                                    >
                                      1
                                    </button>
                                  </li>
                                );
                                
                                if (highStockCurrentPage > 3) {
                                  pages.push(
                                    <li key="ellipsis1" className="page-item disabled">
                                      <span 
                                        className="page-link"
                                        style={{
                                          backgroundColor: isDark ? '#2d2d2d' : '#fff',
                                          borderColor: isDark ? '#444' : '#dee2e6',
                                          color: isDark ? '#fff' : '#000'
                                        }}
                                      >
                                        ...
                                      </span>
                                    </li>
                                  );
                                }
                                
                                const startPage = Math.max(2, highStockCurrentPage - 1);
                                const endPage = Math.min(totalHighStockPages - 1, highStockCurrentPage + 1);
                                
                                for (let i = startPage; i <= endPage; i++) {
                                  pages.push(
                                    <li key={i} className={`page-item ${highStockCurrentPage === i ? 'active' : ''}`}>
                                      <button 
                                        className="page-link"
                                        style={{
                                          backgroundColor: highStockCurrentPage === i 
                                            ? '#0d6efd' 
                                            : (isDark ? '#2d2d2d' : '#fff'),
                                          borderColor: isDark ? '#444' : '#dee2e6',
                                          color: highStockCurrentPage === i ? '#fff' : (isDark ? '#fff' : '#000')
                                        }}
                                        onClick={() => setHighStockCurrentPage(i)}
                                      >
                                        {i}
                                      </button>
                                    </li>
                                  );
                                }
                                
                                if (highStockCurrentPage < totalHighStockPages - 2) {
                                  pages.push(
                                    <li key="ellipsis2" className="page-item disabled">
                                      <span 
                                        className="page-link"
                                        style={{
                                          backgroundColor: isDark ? '#2d2d2d' : '#fff',
                                          borderColor: isDark ? '#444' : '#dee2e6',
                                          color: isDark ? '#fff' : '#000'
                                        }}
                                      >
                                        ...
                                      </span>
                                    </li>
                                  );
                                }
                                
                                pages.push(
                                  <li key={totalHighStockPages} className={`page-item ${highStockCurrentPage === totalHighStockPages ? 'active' : ''}`}>
                                    <button 
                                      className="page-link"
                                      style={{
                                        backgroundColor: highStockCurrentPage === totalHighStockPages 
                                          ? '#0d6efd' 
                                          : (isDark ? '#2d2d2d' : '#fff'),
                                        borderColor: isDark ? '#444' : '#dee2e6',
                                        color: highStockCurrentPage === totalHighStockPages ? '#fff' : (isDark ? '#fff' : '#000')
                                      }}
                                      onClick={() => setHighStockCurrentPage(totalHighStockPages)}
                                    >
                                      {totalHighStockPages}
                                    </button>
                                  </li>
                                );
                              }
                              
                              return pages;
                            })()}
                            
                            <li className={`page-item ${highStockCurrentPage === totalHighStockPages ? 'disabled' : ''}`}>
                              <button 
                                className="page-link"
                                style={{
                                  backgroundColor: isDark ? '#2d2d2d' : '#fff',
                                  borderColor: isDark ? '#444' : '#dee2e6',
                                  color: isDark ? '#fff' : '#000'
                                }}
                                onClick={() => setHighStockCurrentPage(highStockCurrentPage + 1)}
                                disabled={highStockCurrentPage === totalHighStockPages}
                              >
                                Next
                              </button>
                            </li>
                            <li className={`page-item ${highStockCurrentPage === totalHighStockPages ? 'disabled' : ''}`}>
                              <button 
                                className="page-link"
                                style={{
                                  backgroundColor: isDark ? '#2d2d2d' : '#fff',
                                  borderColor: isDark ? '#444' : '#dee2e6',
                                  color: isDark ? '#fff' : '#000'
                                }}
                                onClick={() => setHighStockCurrentPage(totalHighStockPages)}
                                disabled={highStockCurrentPage === totalHighStockPages}
                              >
                                Last
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}